/**
 * Client-side iCalendar (RFC 5545) generation for trip activities (T1.2 / REQ-61).
 *
 * The .ics is built from the SAME activity items the trip-details page already
 * loaded — no server endpoint, no calendar-specific store (REQ-61 single source
 * of truth). A future server `calendar.ics` endpoint would be needed only for
 * webcal:// subscription (deferred with Google Calendar sync).
 */

import { Activity } from '../api/tripsApi';

const DEFAULT_DURATION_MS = 60 * 60 * 1000; // 1h default when an activity has no end

// RFC 5545 TEXT escaping: backslash, semicolon, comma, and newlines.
function escapeText(value: string): string {
  return (value || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

// UTC timestamp form: YYYYMMDDTHHMMSSZ
function toUtcStamp(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

// Fold lines to <=75 octets with CRLF + leading space, per RFC 5545 §3.1.
function foldLine(line: string): string {
  if (line.length <= 75) return line;
  const chunks: string[] = [];
  let remaining = line;
  chunks.push(remaining.slice(0, 75));
  remaining = remaining.slice(75);
  while (remaining.length > 74) {
    chunks.push(' ' + remaining.slice(0, 74));
    remaining = remaining.slice(74);
  }
  if (remaining.length) chunks.push(' ' + remaining);
  return chunks.join('\r\n');
}

function activityLocationText(activity: Activity): string {
  return typeof activity.location === 'string' ? activity.location : '';
}

/**
 * A calendar-ready event: an already-resolved instant, independent of where it
 * came from (a server activity, a day-plan row, a locally added calendar block).
 */
export interface CalendarEventDraft {
  /** Stable per-event id — becomes the UID, so a re-import updates in place. */
  uid: string;
  title: string;
  start: Date;
  end?: Date;
  location?: string;
  description?: string;
}

/** Build a VCALENDAR string from events that already carry resolved instants. */
export function buildIcsForEvents(
  events: CalendarEventDraft[],
  calendarName: string
): string {
  const now = toUtcStamp(new Date());
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Wayplume//Trips//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeText(calendarName)}`,
  ];

  for (const event of events) {
    // Use the event's real end when present and valid; otherwise a
    // default-length block.
    const end = event.end && !Number.isNaN(event.end.getTime()) && event.end > event.start
      ? event.end
      : new Date(event.start.getTime() + DEFAULT_DURATION_MS);

    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${event.uid}@wayplume-trips`);
    lines.push(`DTSTAMP:${now}`);
    lines.push(`DTSTART:${toUtcStamp(event.start)}`);
    lines.push(`DTEND:${toUtcStamp(end)}`);
    lines.push(`SUMMARY:${escapeText(event.title || 'Activity')}`);
    if (event.location) lines.push(`LOCATION:${escapeText(event.location)}`);
    if (event.description) lines.push(`DESCRIPTION:${escapeText(event.description)}`);
    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');
  return lines.map(foldLine).join('\r\n') + '\r\n';
}

/**
 * Build a VCALENDAR string. Only activities with a valid `dateTime` become
 * timed VEVENTs (an undated activity can't be placed on a calendar). Returns
 * the .ics text and how many activities were included/skipped.
 */
export function buildIcs(
  activities: Activity[],
  calendarName: string
): { ics: string; included: number; skipped: number } {
  const events: CalendarEventDraft[] = [];
  let skipped = 0;

  for (const activity of activities) {
    const start = activity.dateTime ? new Date(activity.dateTime) : null;
    if (!start || Number.isNaN(start.getTime())) {
      skipped += 1;
      continue;
    }
    const explicitEnd = activity.endDateTime ? new Date(activity.endDateTime) : null;

    events.push({
      uid: activity.id,
      title: activity.title,
      start,
      end: explicitEnd && !Number.isNaN(explicitEnd.getTime()) ? explicitEnd : undefined,
      location: activityLocationText(activity),
    });
  }

  return { ics: buildIcsForEvents(events, calendarName), included: events.length, skipped };
}

/** Turn a calendar/event name into a safe .ics filename stem. */
export function toIcsFileName(name: string): string {
  const stem = (name || '').replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
  return `${stem || 'calendar'}.ics`;
}

/**
 * Hand an .ics to the OS. A blob download is what Apple Calendar (macOS) and
 * iOS both open natively — top-level `data:` URLs are blocked by Safari, so the
 * object URL is the only path that works on iPhone.
 */
export function downloadIcs(ics: string, fileName: string): void {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Build the .ics and trigger a browser download. Returns the include/skip counts. */
export function downloadTripCalendar(
  activities: Activity[],
  tripTitle: string
): { included: number; skipped: number } {
  const name = tripTitle?.trim() || 'Trip';
  const { ics, included, skipped } = buildIcs(activities, name);

  downloadIcs(ics, toIcsFileName(name));

  return { included, skipped };
}
